import {
  levelOneFor,
  type LevelOne,
  type PreviewRole,
  type RoleAssignment,
  type RoleKey,
} from '@myrewards/contracts';
import { homeLayout, homeSectorAssignments, WAITING_ROLES } from '../home/layout';
import { PREVIEW_ROLE_LABEL } from '../session/presenter';
import { LEVEL_ONE } from '../shell/levelOne';

/**
 * Preview as (TEST1, APP-059) as pure rules: the role cards and presets, and what a combination
 * shows. "Header will show" comes from `levelOneFor`, the rule the server sends `nav` from;
 * "Home will stack" comes from Home's own layout rules. Nothing here is hand-tagged per role, so
 * the hints, the summary and the sample pages can't disagree.
 */

type Ref = { id: string; name: string };

/** The roles a tester can add to Employee, in role order. */
export const PREVIEW_ROLES: readonly PreviewRole[] = [
  'manager',
  'sector_lead',
  'delegate',
  'executive_delegate',
  'executive',
  'stock_admin',
  'payroll',
  'finance',
  'system_admin',
  'support',
];

/** Roles that work in one sector: choosing one asks "… for which sector?". */
export const SCOPED_ROLES: readonly PreviewRole[] = ['sector_lead', 'delegate'];

/** One line under each role's name: what the role brings. No real person is ever named. */
export const ROLE_NOTE: Record<PreviewRole, string> = {
  manager: 'Letters to hand out to your team',
  sector_lead: 'Sector pool, approvals, team recognition',
  delegate: 'Enters awards for a Sector Lead',
  executive_delegate: 'Enters awards for an Executive',
  executive: 'Approvals and every pool',
  stock_admin: 'Award queue, letters, Shareworks',
  payroll: 'Payroll queue, earning statements',
  finance: 'Spend, budgets, charge codes',
  system_admin: 'All Settings, people, directory sync, roles and audit log',
  support: 'Reviews feedback in Settings',
};

export interface PreviewPreset {
  id: string;
  label: string;
  roles: readonly PreviewRole[];
}

/** One-click combinations the testers use most. */
export const PRESETS: readonly PreviewPreset[] = [
  { id: 'employee', label: 'Employee only', roles: [] },
  { id: 'manager', label: 'Manager', roles: ['manager'] },
  { id: 'sector-lead', label: 'Sector Lead', roles: ['sector_lead'] },
  {
    id: 'executive-sector-lead',
    label: 'Executive + Sector Lead',
    roles: ['sector_lead', 'executive'],
  },
  { id: 'stock-admin', label: 'Stock Admin', roles: ['stock_admin'] },
  { id: 'payroll-finance', label: 'Payroll + Finance', roles: ['payroll', 'finance'] },
];

/** What the panel is about to start: roles on top of Employee, and the sector when scoped. */
export interface PreviewDraft {
  roles: ReadonlySet<PreviewRole>;
  sector: Ref | null;
}

/** Employee first, then the picked roles in role order. */
export function draftRoles(draft: Pick<PreviewDraft, 'roles'>): RoleKey[] {
  return ['employee', ...PREVIEW_ROLES.filter((r) => draft.roles.has(r))];
}

export function isScoped(roles: Iterable<RoleKey>): boolean {
  for (const r of roles) if ((SCOPED_ROLES as readonly RoleKey[]).includes(r)) return true;
  return false;
}

/** The roles a combination presents as, exactly as the server's `GET /me` sends them. */
export function draftAssignments(roles: readonly RoleKey[], sector: Ref | null): RoleAssignment[] {
  return roles.map((role) => ({
    role,
    sector: (SCOPED_ROLES as readonly RoleKey[]).includes(role) ? sector : null,
    forPerson: null,
    needsApproval: null,
    awardTypes: null,
  }));
}

/** The Level 1 labels the header will show, in order. */
export function navLabels(nav: readonly LevelOne[]): string[] {
  return LEVEL_ONE.filter((item) => nav.includes(item.id)).map((item) => item.label);
}

/** The hints on a role's card: the Level 1 items it adds, and whether Home becomes a queue. */
export function roleHints(role: PreviewRole): string[] {
  const base = new Set(levelOneFor(['employee']));
  const added = navLabels(levelOneFor(['employee', role]).filter((item) => !base.has(item)));
  const assignments = draftAssignments(['employee', role], { id: 'sector', name: 'Sector' });
  const queue = homeLayout(assignments) === 'queue';
  return [
    ...(queue ? ['Home = queue'] : []),
    ...(role === 'system_admin' ? ['Adds System administration in Settings'] : []),
    ...(role === 'support' ? ['Adds Feedback in Settings'] : []),
    ...(homeLayout(assignments) === 'finance' ? ['Home = Spend dashboard'] : []),
    ...(added.length > 0 ? [`Adds ${added.join(', ')}`] : []),
  ];
}

/** One card on Home, in the order Home stacks them. */
export interface HomeStackCard {
  id: string;
  title: string;
  /** What the card holds, in a line. */
  holds: string;
  /** How the live summary names it ("Waiting on you (from: Sector Lead, Employee)"). */
  summary: string;
  /** The coloured rule along the top: gold for Waiting on you and your own awards. */
  accent: 'gold' | 'cyan' | 'sky';
}

/** "Sector Lead, Employee": the roles Waiting on you gathers from, in role order, Employee last. */
function waitingFrom(roles: readonly RoleKey[]): string {
  const elevated = WAITING_ROLES.filter((r) => r !== 'employee' && roles.includes(r));
  return [...elevated, 'employee' as const].map((r) => PREVIEW_ROLE_LABEL[r]).join(', ');
}

function card(
  id: string,
  title: string,
  holds: string,
  accent: HomeStackCard['accent'],
  summary = title,
): HomeStackCard {
  return { id, title, holds, summary, accent };
}

export interface HomeStack {
  /** Where Home opens: its own page or a queue. */
  opensOn: 'home' | 'stock_admin_queue' | 'payroll_queue';
  cards: HomeStackCard[];
}

/** What Home will stack for a role mix, top to bottom, from the rules Home itself renders by. */
export function homeStack(assignments: readonly RoleAssignment[]): HomeStack {
  const roles = assignments.map((a) => a.role);
  const from = waitingFrom(roles);
  const waiting = card(
    'waiting',
    'Waiting on you',
    `From: ${from}`,
    'gold',
    `Waiting on you (from: ${from})`,
  );
  const folded = { ...waiting, holds: `${waiting.holds} · folded above the queue` };
  if (homeLayout(assignments) === 'finance') {
    return {
      opensOn: 'home',
      cards: [
        waiting,
        card(
          'spend',
          'Spend dashboard',
          'Spend by pool, award type, charge code, month, granter and recipient',
          'cyan',
        ),
      ],
    };
  }
  // The Executive's dashboard (X1) and a requester's requests (R1), in the order Home draws them.
  const executive = roles.includes('executive')
    ? [
        card(
          'executive',
          'Executive dashboard',
          'Setup for the year, every budget pool, recent awards, final summaries, Sector Leads, recognition coverage',
          'cyan',
        ),
      ]
    : [];
  const requests =
    roles.includes('executive_delegate') && !roles.includes('executive')
      ? [
          card(
            'requests',
            'My requests',
            'Executive Discretionary Bonus requests and where each one is, and how requests work',
            'cyan',
          ),
        ]
      : [];
  const sectors = homeSectorAssignments(assignments).map((a) =>
    card(
      `pool-${a.sector?.id ?? 'sector'}`,
      `${a.sector?.name ?? 'Sector'} pool`,
      a.role === 'sector_lead'
        ? 'Sector Lead · budget, Recognize, team recognition, most recognized'
        : 'Delegate · budget and Recognize, entering for the Sector Lead',
      'sky',
    ),
  );
  // APP-148 D2 (R11): what Home adds once Stock Admins can act for others. A Stock Admin sees the
  // Executives' and Sector Leads' work under Waiting on you; an Executive or a Sector Lead sees
  // what was done for them.
  const others = roles.includes('stock_admin')
    ? [
        card(
          'waiting-on-others',
          'Waiting on others',
          'The Executives’ decisions and the Sector Leads’ work, oldest first, each saying whose it is, with Open. Not counted in your bell or in Waiting on you',
          'cyan',
        ),
        // R82: under it, every pool's cash for the year (the Home board).
        card(
          'budget-pools',
          'Budget pools',
          'Each pool’s cash budget, in progress, paid and cash left for the year; a pool its awards would take below zero says Over budget if approved, and Budgets opens them all',
          'sky',
        ),
      ]
    : [];
  const doneForYou =
    roles.includes('executive') ||
    homeSectorAssignments(assignments).some((a) => a.role === 'sector_lead')
      ? [
          card(
            'done-for-you',
            'Done for you · last 30 days',
            'What the Stock Admin team did when you asked them to, with their note and any files; Open and Not what you asked? on each, and See them all',
            'cyan',
          ),
        ]
      : [];
  const layout = homeLayout(assignments);
  if (layout === 'queue') {
    return {
      opensOn: 'stock_admin_queue',
      cards: [
        folded,
        ...others,
        card(
          'queue',
          'Stock Admin queue',
          'Approved awards to claim and check · views in a left sidebar · today’s activity and who’s working',
          'cyan',
          'Stock Admin queue (opens here)',
        ),
        ...doneForYou,
        ...executive,
        ...sectors,
        ...requests,
      ],
    };
  }
  if (layout === 'blended') {
    return {
      opensOn: 'home',
      cards: [
        waiting,
        ...doneForYou,
        ...executive,
        ...sectors,
        ...requests,
        card('my-recognition', 'My recognition', 'Your awards, elections and letters', 'gold'),
      ],
    };
  }
  return {
    opensOn: 'home',
    cards: [
      waiting,
      card(
        'my-awards',
        'My awards',
        'Every award you’ve received, newest first, and where each one is',
        'gold',
      ),
      card(
        'spot',
        'Spot nomination',
        'Nominate a colleague for work that stood out, when nominations are open',
        'sky',
      ),
      card('documents', 'Documents', 'Your award letters and earning statements', 'sky'),
    ],
  };
}
